import { post, get } from './client';

// 身份验证请求数据
export interface IdentityVerificationRequest {
  fullName: string;
  idCard: string;
  phone: string;
  backupPhone?: string;
}

// 身份验证响应（unwrapResponse 已解包 data）
export interface IdentityVerificationResponse {
  verified: boolean;
  message: string;
}

// 系统检测请求数据
export interface SystemCheckRequest {
  fullName: string;
  phone: string;
  idCard: string;
  email?: string;
  // 备用手机号：用于多身份检测（规则 3）交叉命中 Party.phone / Party.backup_phone，
  // 确保“同一自然人分两个手机号分别被登记”的场景能被完整曝露为多身份候选
  backupPhone?: string;
}

// 命中明细字段（认领确认页「命中信息」展示用，与后端 match-fields.util.ts 对应）
export interface MatchedField {
  field: 'fullName' | 'phone' | 'backupPhone' | 'email' | 'idCard';
  label: string;
  matchWay: 'exact' | 'homophone' | 'abbreviated';
  detail: string | null;
}

// 系统检测命中的档案实体
export interface MatchedEntityInfo {
  id: string;
  fullName: string;
  phone: string;
  backupPhone?: string;
  email?: string;
  idCard?: string;
  username?: string;
  avatar?: string | null;
  // 逐字段命中明细（含联系方式主备交叉方向、姓名三档匹配方式）
  matchedFields?: MatchedField[];
}

// 系统检测响应
export interface SystemCheckResponse {
  exists: boolean;
  isRegistered: boolean;
  multipleIdentities: boolean;
  matchType?: 'idCard' | 'idCard+namePhone' | 'namePhone' | 'phoneOnly' | string;
  // 主手机与命中档案不一致（升格换号）→ 注册需短信验证新手机持有权
  phoneChanged?: boolean;
  // 仅手机号命中（姓名不同）→ 认领确认页：照片比对后由注册用户判定是否本人
  phoneOnlyMatch?: boolean;
  matchedEntity?: MatchedEntityInfo;
  matchedEntities?: MatchedEntityInfo[];
  // 人工复核触发条件：身份证号撞库但姓名/手机号不符、邮箱/用户名占用无身份锚点等
  requiresReview?: boolean;
  reviewReason?: 'idcard_conflict' | 'contact_conflict' | string;
  conflictEntities?: Array<{
    id: string;
    fullName: string;
    phone?: string;
    backupPhone?: string;
    email?: string;
    idCard?: string;
    username?: string;
  }>;
  // 老手机号被新用户回收的提示标志（不阻塞注册）
  phoneRecycled?: boolean;
  // 规则 4：强制要求先通过手机号短信验证才能提交注册（证明号码归属）
  releaseRequired?: boolean;
  // 脱敏提示：用于前端文案引导、不暴露旧持有人真实全名
  phoneConflictHint?: {
    oldOwnerNameMasked: string;
    hasEmail: boolean;
    // 释放验证的整句说明：服务端下发，前端逐字显示、不再自写
    releaseNotice?: string;
  };
}

// 用户名检查响应
export interface CheckUsernameResponse {
  available: boolean;
  message: string;
  status: 'available' | 'user';
}

// 邮箱检查响应
export interface CheckEmailResponse {
  available: boolean;
  message: string;
  status: 'available' | 'customer' | 'user';
}

// 手机号检查响应
export interface CheckPhoneResponse {
  available: boolean;
  message: string;
  status: 'available' | 'customer' | 'user';
}

// 身份证号检查响应
export interface CheckIdCardResponse {
  available: boolean;
  message: string;
  status: 'available' | 'customer' | 'user';
}

// 注册请求数据（五步制：信息步已完成三要素验证与邮箱码验证）
// 称呼（displayName）不由用户填写：仅在升格/合并场景由后端按姓名三档规则自动生成
export interface RegisterRequest {
  fullName: string;
  phone: string;
  backupPhone?: string;
  idCard: string;
  username: string;
  password: string;
  email: string;
  // 信息步发送并填写的 6 位邮箱验证码（后端一次性消费）
  emailCode: string;
  // 升格换号场景：主手机与档案不一致时的短信验证码
  smsCode?: string;
  customerId?: string;
  primaryIdentityId?: string;
  // 规则 4 配套：手机号释放凭证（由 verifyPhoneRelease 返回，10 分钟有效）
  releaseToken?: string;
  // 认领弹窗「非本人信息」申诉凭证：验证通过仅持有，注册提交时才腾位/停用原档案
  disputeRelease?: string;
  // 活动标识（?campaign= 或活动落地页捕获，见 hooks/useCampaignCapture）；不命中/已下线时后端按无活动处理
  campaign?: string;
}

// 注册成功响应（unwrapResponse 已解包 data；「注册即登录」返回双令牌与流程摘要）
export interface RegisterSuccessData {
  id: string;
  username: string;
  fullName: string;
  displayName?: string;
  phone: string;
  backupPhone?: string;
  idCard?: string;
  email?: string;
  role: string;
  isUser: boolean;
  status?: string;
  userLevel?: string;
  /**
   * 注册即登录签发的令牌。refreshToken 不在其中——后端
   * registration.controller.ts 会先把它从响应体删除，再经 deliverRefreshToken
   * 写入 HttpOnly Cookie，前端读不到也不该读。
   */
  tokens: {
    accessToken: string;
  };
  summary: {
    isNewUser: boolean;
    upgradedFromCustomer: boolean;
    mergedCount: number;
    ordersMoved: number;
    displayName: string;
  };
  message?: string;
}

// 人工申请请求
export interface ManualApplicationRequest {
  /** 申请人自填用户名（注册表单第一步填写），审核通过后账号即用该用户名 */
  username?: string;
  fullName: string;
  phone: string;
  idCard: string;
  backupPhone?: string;
  email: string;
  reason: string;
  additionalInfo?: string;
  /** 身份证照片相对 URL（POST /manual-application/upload-id-card 返回） */
  idCardPhoto?: string;
}

/**
 * 上传人工审核申请的身份证照片（公开接口，申请人尚未登录）
 * 返回照片相对 URL（/uploads/id-cards/...，静态访问受 JWT 保护，仅管理员可查看）
 */
export async function uploadManualApplicationPhoto(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const data = await post<{ url: string }>('/manual-application/upload-id-card', formData);
  return data.url;
}

// 人工申请响应
export interface ManualApplicationResponse {
  success: boolean;
  message: string;
  applicationId?: string;
}

// 身份合并请求
/**
 * 合并短信验证项：姓名不同、仅联系方式命中的次档，
 * 合并前须凭该号码收到的短信验证码证明号码持有（防备用电话填错并入他人档案）。
 */
export interface MergeSmsVerification {
  secondaryId: string;
  phone: string;
  code: string;
}

export interface MergeIdentitiesRequest {
  primaryId: string;
  entityIds: string[];
  /**
   * 注册表单数据：后端 `POST /auth/identity/merge` 要求必传（用来以注册信息为准覆盖主档字段），
   * 字段名用 camelCase，全局 RequestInterceptor 会统一转 snake_case。
   */
  registerData: {
    fullName?: string;

    phone?: string;
    backupPhone?: string;
    idCard?: string;
    email?: string;
    username?: string;
    password?: string;
    [key: string]: unknown;
  };
  /** 仅号码命中次档的短信验证结果（后端强制校验，缺项返回 requireSmsVerify） */
  verifications?: MergeSmsVerification[];
}

// 合并发码响应（后端 data.requireSmsVerify：待验证次档列表）
export interface MergeSendVerifyCodeResponse {
  success: boolean;
  message: string;
}

// 身份合并响应
export interface MergeIdentitiesResponse {
  mainIdentityId: string;
  mergedIdentityIds: string[];
  summary: {
    mergedCount: number;
    ordersMoved: number;
    tagsMoved?: number;
  };
}


// 邮箱验证码响应
export interface SendEmailCodeResponse {
  success: boolean;
  message: string;
}

// 升格换号短信验证码响应
export interface SendUpgradeSmsResponse {
  success: boolean;
  message: string;
}

/**
 * 验证用户身份信息（手机号三要素核验）
 */
export async function verifyIdentity(data: IdentityVerificationRequest): Promise<IdentityVerificationResponse> {
  return post<IdentityVerificationResponse>('/auth/verify-identity', data);
}

/**
 * 检查系统中是否已存在用户信息
 */
export async function checkSystemIdentity(data: SystemCheckRequest): Promise<SystemCheckResponse> {
  return post<SystemCheckResponse>('/auth/check-identity', data);
}

/**
 * 检查用户名是否可用
 */
export async function checkUsername(username: string): Promise<CheckUsernameResponse> {
  return get<CheckUsernameResponse>('/auth/check-username', { params: { username } });
}

/**
 * 检查邮箱是否可用
 */
export async function checkEmail(email: string): Promise<CheckEmailResponse> {
  return get<CheckEmailResponse>('/auth/check-email', { params: { email } });
}

/**
 * 检查手机号是否可用
 */
export async function checkPhone(phone: string): Promise<CheckPhoneResponse> {
  return get<CheckPhoneResponse>('/auth/check-phone', { params: { phone } });
}

/**
 * 检查身份证号是否可用
 */
export async function checkIdCard(idCard: string): Promise<CheckIdCardResponse> {
  return get<CheckIdCardResponse>('/auth/check-idcard', { params: { idCard } });
}

/**
 * 发送注册邮箱验证码（6 位数字，5 分钟有效，60 秒限发）
 */
export async function sendEmailCode(email: string, fullName?: string): Promise<SendEmailCodeResponse> {
  return post<SendEmailCodeResponse>('/auth/email/send-code', { email, fullName });
}

/**
 * 发送升格换号短信验证码（主手机与档案不一致时验证新手机持有权）
 */
export async function sendUpgradeSmsCode(phone: string): Promise<SendUpgradeSmsResponse> {
  return post<SendUpgradeSmsResponse>('/auth/phone/send-register-code', { phone });
}

/**
 * 提交注册申请（成功直接返回登录令牌，注册即登录）
 */
export async function register(data: RegisterRequest): Promise<RegisterSuccessData> {
  return post<RegisterSuccessData>('/auth/register', data);
}

/**
 * 提交人工身份验证申请
 */
export async function submitManualApplication(data: ManualApplicationRequest): Promise<ManualApplicationResponse> {
  return post<ManualApplicationResponse>('/manual-application', data);
}

/**
 * 合并多个身份信息
 */
export async function mergeIdentities(data: MergeIdentitiesRequest): Promise<MergeIdentitiesResponse> {
  // 转换数据格式以匹配后端 API
  const mergeDto = {
    identities: [
      { id: data.primaryId },
      ...data.entityIds.map(id => ({ id }))
    ],
    // 透传注册表单数据，由全局 RequestInterceptor 统一转 snake_case。
    // 后端会根据这些字段把主档的 full_name/phone/id_card/email 等按注册信息覆盖。
    registerData: data.registerData || {},
    // 仅号码命中次档的短信验证（camelCase → 全局拦截器转 snake_case）
    verifications: data.verifications || []
  };
  return post<MergeIdentitiesResponse>('/auth/identity/merge', mergeDto);
}

/**
 * 发送身份合并短信验证码（姓名不同、仅联系方式命中的次档验证号码持有）
 */
export async function sendMergeVerifyCode(data: {
  secondaryId: string;
  phone: string;
  registerData: MergeIdentitiesRequest['registerData'];
}): Promise<MergeSendVerifyCodeResponse> {
  return post<MergeSendVerifyCodeResponse>('/auth/identity/merge/send-verify-code', data);
}


// =============================================================================
//  手机号释放流程（规则 4 配套）
// =============================================================================

export interface SendPhoneReleaseCodeRequest {
  phone: string;
  fullName: string;
  idCard?: string;
}

export interface SendPhoneReleaseCodeResponse {
  expireSeconds: number;
}

export interface VerifyPhoneReleaseRequest {
  phone: string;
  code: string;
  fullName: string;
  idCard?: string;
}

export interface VerifyPhoneReleaseResponse {
  releaseToken: string;
  expiresIn: number;
}

/**
 * 发送手机号释放短信验证码
 * -- 使用场景：check-identity 返回 releaseRequired=true 时调用
 */
export async function sendPhoneReleaseCode(
  data: SendPhoneReleaseCodeRequest
): Promise<SendPhoneReleaseCodeResponse> {
  return post<SendPhoneReleaseCodeResponse>('/auth/phone/send-release-code', data);
}

/**
 * 校验手机号释放验证码
 * -- 返回 releaseToken，随后传入 register(..., releaseToken) 完成注册
 */
export async function verifyPhoneRelease(
  data: VerifyPhoneReleaseRequest
): Promise<VerifyPhoneReleaseResponse> {
  return post<VerifyPhoneReleaseResponse>('/auth/phone/verify-release', data);
}

